// BookingComponent.jsx - Versione con controllo idoneità prenotazione
import React, { useState, useEffect } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { AlertCircle, User } from 'lucide-react';
import PropTypes from 'prop-types';
import DoctorCalendarService from '../../services/doctor.calendar.service';
import PatientService from '../../services/patient.service';

const BookingComponent = ({ services, doctorId, onBookingConfirm }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Nuovo stato per controllo idoneità
  const [bookingEligibility, setBookingEligibility] = useState({
    can_book: false,
    missing_fields: [],
    loading: true,
    message: ''
  });

  // Definizione delle fasce orarie - utilizzate solo internamente
  const TIME_SLOTS = {
    MORNING: { start: '07:00', end: '14:00' },
    AFTERNOON: { start: '14:00', end: '19:00' },
    EVENING: { start: '19:00', end: '23:59' }
  };

  // Controlla l'idoneità alla prenotazione al caricamento del componente
  useEffect(() => {
    checkBookingEligibility();
  }, []);

  // Al caricamento del componente, ottieni le date disponibili per il mese corrente
  useEffect(() => {
    if (doctorId && bookingEligibility.can_book) {
      fetchAvailableDates();
    }
  }, [doctorId, bookingEligibility.can_book]);

  // Quando viene selezionata una data, carica gli slot disponibili per quella data
  useEffect(() => {
    if (selectedDate && bookingEligibility.can_book) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, bookingEligibility.can_book]);

  const checkBookingEligibility = async () => {
    try {
      const eligibilityData = await PatientService.checkBookingEligibility();
      setBookingEligibility({
        can_book: eligibilityData.data.can_book,
        missing_fields: eligibilityData.data.missing_fields,
        message: eligibilityData.data.message,
        loading: false
      });
    } catch (error) {
      console.error('Errore nel controllo idoneità:', error);
      setBookingEligibility({
        can_book: false,
        missing_fields: ['errore_controllo'],
        message: 'Errore nel controllo dei dati',
        loading: false
      });
    }
  };

  // Funzione per determinare la fascia oraria corrente - utilizzata solo internamente
  const getCurrentTimeSlot = () => {
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    
    if (currentTime >= TIME_SLOTS.MORNING.start && currentTime < TIME_SLOTS.AFTERNOON.start) {
      return 'MORNING';
    } else if (currentTime >= TIME_SLOTS.AFTERNOON.start && currentTime < TIME_SLOTS.EVENING.start) {
      return 'AFTERNOON';
    } else {
      return 'EVENING';
    }
  };

  const fetchAvailableDates = async () => {
    setLoading(true);
    setError('');
    try {
      // Ottieni il primo e l'ultimo giorno del periodo di interesse (oggi + 30 giorni)
      const today = new Date();
      const tomorrow = addDays(today, 1); // Inizia dal giorno dopo
      const startDate = format(tomorrow, 'yyyy-MM-dd');
      const endDate = format(addDays(today, 30), 'yyyy-MM-dd'); // Prossimi 30 giorni

      const response = await DoctorCalendarService.getAvailabilities(
        doctorId, 
        startDate, 
        endDate
      );

      // Estrai date uniche dagli slot disponibili
      const uniqueDates = Array.from(new Set(response.available_slots.map(slot => slot.date)));
      const sortedDates = uniqueDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      
      // Converti le stringhe di date in oggetti Date
      const validDates = sortedDates.map(date => parseISO(date));
      
      setAvailableDates(validDates);
    } catch (err) {
      console.error('Errore nel caricamento delle date disponibili:', err);
      setError('Impossibile caricare le date disponibili. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    setError('');
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const nextDayStr = format(addDays(date, 1), 'yyyy-MM-dd');

      const response = await DoctorCalendarService.getAvailabilities(
        doctorId, 
        dateStr, 
        nextDayStr
      );

      // Filtra solo gli slot per la data selezionata
      const slotsForDate = response.available_slots.filter(slot => slot.date === dateStr);
      
      // Determina la fascia oraria corrente
      const currentTimeSlot = getCurrentTimeSlot();
      
      // Filtra gli slot in base alla fascia oraria corrente e se la data è domani
      let filteredSlots = slotsForDate;
      
      // Se la data selezionata è domani, applica la regola delle fasce orarie
      const tomorrow = addDays(new Date(), 1);
      if (format(date, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd')) {
        switch (currentTimeSlot) {
          case 'MORNING':
            // Se siamo di mattina, mostra tutte le fasce orarie di domani
            break;
          case 'AFTERNOON':
            // Se siamo di pomeriggio, mostra solo pomeriggio e sera di domani
            filteredSlots = slotsForDate.filter(slot => 
              slot.start >= TIME_SLOTS.AFTERNOON.start
            );
            break;
          case 'EVENING':
            // Se siamo di sera, mostra solo sera di domani
            filteredSlots = slotsForDate.filter(slot => 
              slot.start >= TIME_SLOTS.EVENING.start
            );
            break;
        }
      }
      
      // Ordina gli slot per orario
      const sortedSlots = filteredSlots.sort((a, b) => {
        return a.start.localeCompare(b.start);
      });

      setAvailableSlots(sortedSlots);
    } catch (err) {
      console.error('Errore nel caricamento degli slot disponibili:', err);
      setError('Impossibile caricare gli orari disponibili. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return format(date, 'EEEE, d MMM', { locale: it });
  };

  const isSameDay = (date1, date2) => {
    return date1 && date2 && format(date1, 'yyyy-MM-dd') === format(date2, 'yyyy-MM-dd');
  };

  // Mostra fino a 20 date disponibili (invece di 10)
  const visibleDates = availableDates.slice(0, 20);

  // Componente per avviso dati mancanti
  const BookingRestrictionNotice = () => (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
      <div className="flex items-start">
        <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">
            Completa il tuo profilo per prenotare
          </h3>
          <p className="text-amber-700 mb-4">
            Per poter prenotare appuntamenti è necessario compilare i seguenti campi nel tuo profilo:
          </p>
          <ul className="list-disc list-inside text-amber-700 mb-4 space-y-1">
            <li>I tuoi dati anagrafici</li>
            <li>Almeno uno tra Codice Fiscale (CF) e Partita IVA (P.IVA)</li>
            <li>Il tuo indirizzo di residenza</li>
          </ul>
          <div className="flex items-center space-x-3">
            <a 
              href="/patient/profile" 
              className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
            >
              <User className="w-4 h-4 mr-2" />
              Completa Profilo
            </a>
            <button 
              onClick={checkBookingEligibility}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Ricontrolla
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Loading dell'idoneità
  if (bookingEligibility.loading) {
    return (
      <div className="bg-white p-6 rounded-md shadow-md">
        <div className="text-center py-8">
          <svg className="animate-spin h-8 w-8 mx-auto text-purple-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Controllo idoneità alla prenotazione...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-md shadow-md">
      {/* Mostra avviso se non può prenotare */}
      {!bookingEligibility.can_book && <BookingRestrictionNotice />}
      
      {/* Contenuto principale - disabilitato se non può prenotare */}
      <div className={bookingEligibility.can_book ? '' : 'opacity-50 pointer-events-none'}>
        <div className="mb-6">
          <h3 className="font-semibold mb-4">Seleziona Servizio</h3>
          <select 
            className="w-full p-2 border rounded-md"
            onChange={(e) => setSelectedService(services.find(s => s.id === parseInt(e.target.value)))}
            disabled={loading || !bookingEligibility.can_book}
          >
            <option value="">Seleziona un servizio</option>
            {(services || []).map(service => (
              <option key={service.id} value={service.id}>
                {service.name} - {service.duration}min - €{service.price}
              </option> 
            ))}
          </select>
        </div>

        {selectedService && bookingEligibility.can_book && (
          <>
            <div className="mb-6">
              <h3 className="font-semibold mb-4">Seleziona Data</h3>
              
              {loading && !availableDates.length ? (
                <div className="text-center py-4">
                  <svg className="animate-spin h-5 w-5 mx-auto text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-2 text-gray-600">Caricamento date disponibili...</p>
                </div>
              ) : availableDates.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                    {visibleDates.map((date, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedDate(date);
                          setSelectedSlot(''); // Reset selected slot when date changes
                        }}
                        className={`p-4 rounded-md cursor-pointer w-full ${
                          isSameDay(date, selectedDate) ? 'bg-purple-100' : 'bg-gray-50'
                        }`}
                      >
                        {formatDate(date)}
                      </div>
                    ))}
                  </div>
              ) : (
                <p className="text-center py-4 text-gray-600">
                  {error || "Nessuna data disponibile nei prossimi giorni"}
                </p>
              )}
            </div>

            {selectedDate && (
              <div className="mb-6">
                <h3 className="font-semibold mb-4">Seleziona Orario</h3>
                {loading && !availableSlots.length ? (
                  <div className="text-center py-4">
                    <svg className="animate-spin h-5 w-5 mx-auto text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="mt-2 text-gray-600">Caricamento orari disponibili...</p>
                  </div>
                ) : availableSlots.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {availableSlots.map(slot => (
                      <button
                        key={`${slot.date}-${slot.start}`}
                        onClick={() => setSelectedSlot(slot.start)}
                        className={`py-2 px-4 rounded ${
                          selectedSlot === slot.start ? 'bg-purple-600 text-white' : 'bg-gray-100'
                        }`}
                      >
                        {slot.start}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-gray-600">
                    {error || "Nessun orario disponibile per la data selezionata"}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {selectedService && selectedDate && selectedSlot && bookingEligibility.can_book && (
          <button
            onClick={() => onBookingConfirm({
              service_id: selectedService.id,
              service_name: selectedService.name,
              date: format(selectedDate, 'yyyy-MM-dd'),
              time: selectedSlot,
              price: selectedService.price,
              total_price: selectedService.price
            })}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700"
            disabled={loading}
          >
            {loading ? 'Caricamento...' : 'Conferma Prenotazione'}
          </button>
        )}
      </div>
    </div>
  );
};

BookingComponent.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      duration: PropTypes.number,
      price: PropTypes.string
    })
  ),
  doctorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onBookingConfirm: PropTypes.func.isRequired
};

export default BookingComponent;